import { type ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  restrictApi,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import { IPurchaseErrorResponse } from '~/purchase/types';
import { IContainerOrder } from '~/common/types';

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IContainerOrder>,
      IPurchaseErrorResponse
    >
  > & {
    args: {
      id: string;
    };
  };
};

export const useManageContainerOrders = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.containerOrder.detail(
      options.get?.args.id as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IContainerOrder>>(
        `/offers/api/tenants/${tenantCode}/container-orders/${options.get?.args.id as string}`,
      ),
    refetchOnWindowFocus: false,
    retryOnMount: true,
    gcTime: 0,
    staleTime: 0,
    ...options.get,
    enabled: Boolean(options.get?.args.id) && options.get?.enabled,
  });

  return restrictApi(options, {
    get: getQuery,
  });
};
